<!-- @format -->
<template>
    <div class="contact-lists-widget">
        <div class="widget-header">
            <h3>{{ contact.name }}'s lists</h3>
            <div class="svg-container widget-close-button" @click="closeWidget">
                <widget-close-svg />
            </div>
        </div>

        <div class="content-wrapper">
            <loading-svg v-if="saving || loading" />
            <div v-else>
                <app-border-section :topText="`Add/Remove Lists`">
                    <list-search :clientId="contact.clientId" @input="addList($event)" />
                    <div class="lists">
                        <div
                            v-for="list in contact.lists"
                            :key="list.id"
                            class="list"
                            :class="list.class"
                        >
                            {{ list.name }}
                            <x-svg @click.native="removeList(list)" />
                        </div>
                    </div>
                </app-border-section>
            </div>
        </div>
        <div class="widget-footer">
            <div class="cancel-container">
                <app-button class="cancel-btn transparent-btn" @click="closeWidget">
                    Cancel
                </app-button>
            </div>
            <div class="action-container">
                <app-button :disabled="saving || loading" class="save-btn" @click="save()">
                    Save
                </app-button>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button.vue');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg.vue');
const ListSearch = () => import('@/components/list/list-search');
const XSvg = () => import('@/components/svg/connectwise/x-svg');
const LoadingSvg = () => import('@/components/svg/loading-screen-svg');
const AppBorderSection = () => import('@/components/common/app-border-section');

import EventBus from '@/scripts/event-bus.js';

export default {
    name: 'contact-lists-widget',

    components: {
        'app-button': AppButton,
        'widget-close-svg': WidgetCloseSvg,
        'list-search': ListSearch,
        'x-svg': XSvg,
        'loading-svg': LoadingSvg,
        'app-border-section': AppBorderSection,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            loading: true,
            saving: false,
            contact: {
                id: this.widget.contactId,
                lists: [],
                name: null,
            },
        };
    },

    created() {
        this.getContact();
    },

    methods: {
        removeList(list) {
            this.contact.lists.splice(this.contact.lists.indexOf(list), 1);
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        addList(newList) {
            var exists = this.contact.lists.some((list) => {
                return list.id === newList.id;
            });

            if (!exists) {
                newList.class = 'solid-grey-bg';
                this.contact.lists.push(newList);
            }
        },

        save() {
            this.saving = true;

            var request = this.contact.lists.map((list) => list.id);

            this.$api
                .put(`/api/Contacts/${this.contact.id}/Lists`, request)
                .then(() => {
                    this.$helpers.showSuccessNotification(
                        'Your lists have been successfully updated.',
                    );
                    this.closeWidget();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while updating your lists.',
                        { error },
                    );
                })
                .finally(() => {
                    this.saving = false;
                });
        },
        getFilterParam() {
            return `$filter=Id eq ${this.contact.id}`;
        },

        getSelectParam() {
            return `$select=FirstName,LastName,ClientId`;
        },

        getExpandParam() {
            return `$expand=ListContacts($expand=List($select=Id,Name))`;
        },

        getQueryParams() {
            return [
                this.getFilterParam(),
                this.getExpandParam(),
                this.getSelectParam(),
                `$count=true`,
            ].join('&');
        },

        getContact() {
            this.$api
                .get(`/service/Contacts?${this.getQueryParams()}`)
                .then((response) => {
                    var contactsResponse = response.data.value[0];
                    this.contact.clientId = contactsResponse.ClientId;

                    this.contact.name = this.$helpers.getName(
                        contactsResponse.FirstName,
                        contactsResponse.LastName,
                    );

                    var colorClasses = [
                        'solid-red-bg',
                        'solid-orange-bg',
                        'solid-blue-bg',
                        'solid-green-bg',
                        'solid-grey-bg',
                    ];

                    this.contact.lists = contactsResponse.ListContacts.map((lc, index) => {
                        return {
                            id: lc.List.Id,
                            name: lc.List.Name,
                            class: colorClasses[index % 5],
                        };
                    });
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while fetching the lists.',
                        { error },
                    );
                })
                .finally(() => {
                    this.loading = false;
                });
        },
    },
};
</script>
<style>
.contact-lists-widget .cancel-btn {
    background: transparent;
    color: #877d88;
    border: none;
}

.contact-lists-widget .cancel-btn:hover {
    background: #e1e0e5;
}

.contact-lists-widget .save-btn {
    background: #18aeff;
}

.contact-lists-widget .save-btn:hover {
    background: var(--color-fill-accent-skyblue-medium); /*mass replace */
}

.contact-lists-widget .list-search {
    margin-top: 20px;
}

.contact-lists-widget .list-search {
    margin-bottom: 10px;
}

.contact-lists-widget .content-wrapper .app-border-section .top-text {
    font:
        18px Roboto,
        sans-serif;
    font-weight: 500;
}

.contact-lists-widget .lists {
    display: flex;
    flex-wrap: wrap;
    margin: 0 -7px;
}

.contact-lists-widget .lists .list {
    color: #ffffff;
    font-size: 11px;
    padding: 3px 10px;
    border-radius: 15px;
    margin: 0 7px 9px 7px;
    display: flex;
    align-items: center;
}

.contact-lists-widget .lists .list svg {
    margin-left: 12px;
    width: 7px;
    cursor: pointer;
}

.contact-lists-widget .lists .list svg path {
    fill: #ffffff;
}
</style>
